import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

interface ScopeEntity {
  id: string;
  name: string;
}

interface AudiencePickerProps {
  roleScope: string | null;
  scopeType: string;
  scopeId: string | null;
  hnhScopeType: string;
  hnhScopeId: string | null;
  onRoleScopeChange: (roleScope: string | null) => void;
  onScopeChange: (scopeType: string, scopeId: string | null) => void;
  onHnhScopeChange: (scopeType: string, scopeId: string | null) => void;
}

export function AudiencePicker({
  roleScope,
  scopeType,
  scopeId,
  hnhScopeType,
  hnhScopeId,
  onRoleScopeChange,
  onScopeChange,
  onHnhScopeChange,
}: AudiencePickerProps) {
  const [sectors, setSectors] = useState<ScopeEntity[]>([]);
  const [organizations, setOrganizations] = useState<ScopeEntity[]>([]);
  const [teams, setTeams] = useState<ScopeEntity[]>([]);
  const [users, setUsers] = useState<ScopeEntity[]>([]);
  const [samenGroups, setSamenGroups] = useState<ScopeEntity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      const [sectorRes, orgRes, teamRes, userRes, samenRes] = await Promise.all([
        supabase.from('sector').select('id, name').order('name'),
        supabase.from('organization').select('id, name').order('name'),
        supabase.from('team').select('id, name').order('name'),
        supabase.from('profiles').select('id, naam').order('naam'),
        supabase.from('samen').select('id, name').order('name'),
      ]);
      if (sectorRes.data) setSectors(sectorRes.data.map((s) => ({ id: s.id, name: s.name })));
      if (orgRes.data) setOrganizations(orgRes.data.map((o) => ({ id: o.id, name: o.name })));
      if (teamRes.data) setTeams(teamRes.data.map((t) => ({ id: t.id, name: t.name })));
      if (userRes.data)
        setUsers(userRes.data.filter((u) => u.naam).map((u) => ({ id: u.id, name: u.naam! })));
      if (samenRes.data) setSamenGroups(samenRes.data.map((s) => ({ id: s.id, name: s.name })));
    };
    fetchEntities();
  }, []);

  const isHartzorg = roleScope === null || roleScope === 'both';
  const isHnH = roleScope === 'hoofdhartuser' || roleScope === 'both';

  const toggleHartzorg = (checked: boolean) => {
    if (checked) {
      onRoleScopeChange(isHnH ? 'both' : null);
    } else if (isHnH) {
      onRoleScopeChange('hoofdhartuser');
      onScopeChange('global', null);
    }
  };

  const toggleHnH = (checked: boolean) => {
    if (checked) {
      onRoleScopeChange(isHartzorg ? 'both' : 'hoofdhartuser');
    } else if (isHartzorg) {
      onRoleScopeChange(null);
      onHnhScopeChange('global', null);
    }
  };

  const HARTZORG_SCOPE_LABELS: Record<string, string> = {
    global: 'Iedereen',
    sector: 'Sector',
    organization: 'Organisatie',
    team: 'Team',
    user: 'Gebruiker',
  };

  const getHartzorgEntityList = (): ScopeEntity[] => {
    switch (scopeType) {
      case 'sector': return sectors;
      case 'organization': return organizations;
      case 'team': return teams;
      case 'user': return users;
      default: return [];
    }
  };

  return (
    <div className="grid gap-3">
      <Label>Doelgroep</Label>

      {/* Hartzorg toggle */}
      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="toggle-hartzorg" className="font-medium">Hartzorg</Label>
          <Switch
            id="toggle-hartzorg"
            checked={isHartzorg}
            onCheckedChange={toggleHartzorg}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
        {isHartzorg && (
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <Select
                value={scopeType}
                onValueChange={(val) => onScopeChange(val, null)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Iedereen</SelectItem>
                  <SelectItem value="sector">Sector</SelectItem>
                  <SelectItem value="organization">Organisatie</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="user">Gebruiker</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scopeType !== 'global' && (
              <div className="w-56">
                <Select
                  value={scopeId ?? ''}
                  onValueChange={(val) => onScopeChange(scopeType, val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Selecteer ${HARTZORG_SCOPE_LABELS[scopeType]?.toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {getHartzorgEntityList().map((entity) => (
                      <SelectItem key={entity.id} value={entity.id}>
                        {entity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* HoofdnaarHart toggle */}
      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="toggle-hnh" className="font-medium">HoofdnaarHart</Label>
          <Switch
            id="toggle-hnh"
            checked={isHnH}
            onCheckedChange={toggleHnH}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
        {isHnH && (
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <Select
                value={hnhScopeType}
                onValueChange={(val) => onHnhScopeChange(val, null)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Iedereen</SelectItem>
                  <SelectItem value="hoofdhartuser">HnH Gebruikers</SelectItem>
                  <SelectItem value="samen">Samen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hnhScopeType === 'samen' && (
              <div className="w-56">
                <Select
                  value={hnhScopeId ?? ''}
                  onValueChange={(val) => onHnhScopeChange(hnhScopeType, val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer samen groep..." />
                  </SelectTrigger>
                  <SelectContent>
                    {samenGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
